/* eslint-disable max-lines-per-function */
import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import { ChevronRight } from "@mui/icons-material";
import { Grid } from "@mui/material";
import { DatePickerField, InputWithErrorLabel } from "components/haven-design/form/inputs";
import { Viewer } from "components/auth/types";
import {
  Citizenships,
  Ethnicities,
  Races,
  UpdateUserProfile,
  UpdateUserProfileVariables,
  UserProfile as Query,
  UserProfileVariables as Vars,
  UserProfile_userProfile_addresses as Address,
  UserRole,
} from "types/gql-types";
import { MaterialFormikSelect as SelectWithLabel } from "components/haven-design/form/select";
import { ContainerCard } from "components/haven-design/card/index";

import SaveOnChange from "components/haven-design/form/save-on-change";

import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { userProfileQuery as query, updateUserProfileMutation } from "../../queries";
import styles from "../../styles.module.css";
import { propertyManagerProfile, clientProfile } from "./schema";
import NotSet from "./components/not-set";
import GenderDropdown from "./components/gender-dropdown";

type Props = {
  viewer: Viewer
};
const raceOptions = [
  [Races.AMERICAN_INDIAN_ALASKAN_NATIVE, "American Indian or Alaskan Native"],
  [Races.ASIAN, "Asian"],
  [Races.AFRICAN_AMERICAN, "African American"],
  [Races.NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER, "Native Hawaiian or Other Pacific Islander"],
  [Races.WHITE, "White"],
  [Races.OTHER, "Other"],
];
const citizenshipOptions = [
  [Citizenships.US_CITIZEN, "US Citizen"],
  [Citizenships.LEGAL_PERMANENT_RESIDENT, "Legal Permanent Resident"],
  [Citizenships.WORK_AUTH, "Work Auth"],
  [Citizenships.WORK_AUTH_PENDING, "Work Auth (Pending)"],
  [Citizenships.OTHER, "Other"],
];
const ethnicityOptions = [
  [Ethnicities.HISPANIC_OR_LATINO, "Hispanic or Latino"],
  [Ethnicities.NOT_HISPANIC_OR_LATINO, "Non Hispanic"],
];

const getAddressDisplayText =
  (address:Address) => (
    address.address2 ? `${address.address1}, ${address.address2}` : address.address1
  );

const ProfilePage: React.FC<Props> = ({ viewer: { id, roles } }) => {
  const { openSnackbar } = useSnackbar();
  const viewerHasRoles = (...requiredRoles: UserRole[]) =>
    roles.some(role => requiredRoles.includes(role as UserRole));

  const { data, loading, error } = useQuery<Query, Vars>(query, { variables: { id } });
  const [updateUserProfile] =
    useMutation<UpdateUserProfile, UpdateUserProfileVariables>(updateUserProfileMutation);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error.message}</div>;
  if (!data?.userProfile) return <div>Profile failed to load.</div>;

  const { userProfile } = data;

  const onSubmit = async (values: typeof userProfile) => {
    const variables = { id, ...values };

    try {
      const res = await updateUserProfile({ variables, refetchQueries: ["UserProfile"] });

      if (!res.data) {
        openSnackbar({ message: "Uh oh, something went wrong while updating your profile.", status: "error" });
        res.errors?.forEach((err) => logger.sendError(err));

        return;
      }

      openSnackbar({ message: "Profile Updated", status: "success" });
    } catch (e) {
      openSnackbar({ message: "Uh oh, something went wrong while updating your profile.", status: "error" });
      logger.sendError(e as Error);
    }
  };
  const isPropMgr = viewerHasRoles(UserRole.PROPERTY_MANAGER, UserRole.SUPERVISOR);

  const schema = isPropMgr ? propertyManagerProfile : clientProfile;

  return (
    <ContainerCard>
      <Formik
        initialValues={{ ...userProfile }}
        validationSchema={schema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        <Form>
          <SaveOnChange />
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <h1>{userProfile.firstName} {userProfile.lastName}</h1>

              {viewerHasRoles(UserRole.CLIENT) && (
                <p>Editing your profile will update all of your applications as well.</p>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <h1 className={styles.header}>Personal Info</h1>
            </Grid>
            {isPropMgr && (
              <Grid size={{ xs: 12 }}>
                <InputWithErrorLabel label="Title" name="title" type="text" fullWidth />
                <hr />
              </Grid>
            )}
            {viewerHasRoles(UserRole.CLIENT) && (
              <>
                <Grid size={{ xs: 12 }}>
                  <DatePickerField label="Date of birth" name="dateOfBirth" margin="dense" fullWidth />
                  <hr />
                </Grid>
                <GenderDropdown />
                <Grid size={{ xs: 12 }}>
                  <SelectWithLabel title="Ethnicity" name="ethnicity"
                    options={ethnicityOptions
                      .map(([key, value]: string[]) => ({ value: key, label: value }))}
                  />
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SelectWithLabel title="Race" name="race"
                    options={raceOptions
                      .map(([key, value]: string[]) => ({ value: key, label: value }))}
                  />
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SelectWithLabel title="Citizenship" name="citizenship"
                    options={citizenshipOptions
                      .map(([key, value]: string[]) => ({ value: key, label: value }))}
                  />
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h1 className={styles.header}>Contact Info</h1>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>Email</h2>
                  <div className={styles.row}>
                    {userProfile.emails.length === 0 && <NotSet />}
                    {userProfile.emails.map(({ address }) => address).join(", ")}
                  </div>
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>Phone</h2>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <div className={styles.row}>
                    {userProfile.phones.length === 0 && <NotSet />}
                    {userProfile.phones.map(({ number }) => number).join(", ")}
                  </div>
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>Current Address</h2>

                </Grid>
                <Grid container size={{ xs: 12 }} alignItems="center" justifyContent="flex-end">
                  <Grid size={{ xs: 10 }}>
                    <div className={styles.row}>
                      {userProfile.currentAddress ===
              null ? <NotSet /> : getAddressDisplayText(userProfile.currentAddress)}
                    </div>
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <Link to="addresses"><ChevronRight /></Link>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>Mailing Address</h2>

                </Grid>
                <Grid container size={{ xs: 12 }} alignItems="center" justifyContent="flex-end">
                  <Grid size={{ xs: 10 }}>
                    <div className={styles.row}>
                      {userProfile.mailingAddress ===
              null ? <NotSet /> : getAddressDisplayText(userProfile.mailingAddress)}
                    </div>
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <Link to="addresses"><ChevronRight /></Link>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>How can we contact you?</h2>
                  <div className={styles.row}>
                    Application updates
                  </div>

                  <NotSet />
                  <hr />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <h2 className={styles.rowHeader}>How can we contact you?</h2>
                  <div className={styles.row}>
                    Marketing information
                  </div>

                  <NotSet />
                  <hr />
                </Grid>
              </>)}
          </Grid>
        </Form>
      </Formik>
    </ContainerCard>
  );
};

export default ProfilePage;
